import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  useGetApiChannelConnections,
} from "@/lib/api/generated/channels/channels";
import {
  usePostApiChannelDeployments,
} from "@/lib/api/generated/channels/channels";
import { useGetApiAgents } from "@/lib/api/generated/agents/agents";
import type { ControlPlaneAgentRecord } from "@/lib/types/control-plane-agents";
import type {
  ChannelConnectionRecord,
} from "../../../../../packages/shared-types/src/index.js";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";

const createDeploymentSchema = z.object({
  id: z.string().min(1, "ID is required"),
  agentId: z.string().min(1, "Agent is required"),
  channelConnectionId: z.string().min(1, "Connection is required"),
  allowDms: z.boolean(),
  allowGroups: z.boolean(),
  requireMention: z.boolean(),
  status: z.enum(["active", "paused"]).default("active"),
});

type CreateDeploymentForm = z.infer<typeof createDeploymentSchema>;

export function CreateDeploymentPage() {
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const mutation = usePostApiChannelDeployments();
  const { data: agents } = useGetApiAgents<ControlPlaneAgentRecord[]>({
    query: { enabled: Boolean(tenantId) },
  });
  const { data: connections } = useGetApiChannelConnections<ChannelConnectionRecord[]>({
    query: { enabled: Boolean(tenantId) },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateDeploymentForm>({
    resolver: zodResolver(createDeploymentSchema),
    defaultValues: {
      id: `dep_${Date.now()}`,
      allowDms: true,
      allowGroups: false,
      requireMention: false,
      status: "active",
    },
  });

  const onSubmit = async (data: CreateDeploymentForm) => {
    try {
      await mutation.mutateAsync({
        data: {
          id: data.id,
          agentId: data.agentId,
          channelConnectionId: data.channelConnectionId,
          allowDms: data.allowDms,
          allowGroups: data.allowGroups,
          requireMention: data.requireMention,
          status: data.status,
        },
      });
      toast.success("Deployment created");
      navigate("/workspace/deployments");
    } catch {
      toast.error("Failed to create deployment");
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <h1 className="text-lg font-semibold mb-6" style={{ color: "var(--text-strong)" }}>
        New Channel Deployment
      </h1>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <FormField label="ID" error={errors.id?.message}>
              <input {...register("id")} className="form-input font-mono" />
            </FormField>

            <FormField label="Agent" error={errors.agentId?.message}>
              <select {...register("agentId")} className="form-input">
                <option value="">Select an agent...</option>
                {agents?.map((a) => (
                  <option key={a.agentId} value={a.agentId}>
                    {a.name}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Channel Connection" error={errors.channelConnectionId?.message}>
              <select {...register("channelConnectionId")} className="form-input">
                <option value="">Select a connection...</option>
                {connections?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName} ({c.channelType})
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Status">
              <select {...register("status")} className="form-input">
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
            </FormField>

            <div className="grid grid-cols-2 gap-4">
              <CheckboxField label="Allow DMs" {...register("allowDms")} />
              <CheckboxField label="Allow Groups" {...register("allowGroups")} />
              <CheckboxField label="Require Mention" {...register("requireMention")} />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Creating..." : "Create Deployment"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => navigate("/workspace/deployments")}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function FormField({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </label>
      {children}
      {error && (
        <span className="text-xs" style={{ color: "var(--danger)" }}>
          {error}
        </span>
      )}
    </div>
  );
}

const CheckboxField = ({
  label,
  ...props
}: { label: string } & React.InputHTMLAttributes<HTMLInputElement>) => (
  <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: "var(--text-strong)" }}>
    <input type="checkbox" {...props} className="rounded border-border" />
    {label}
  </label>
);
